import React from 'react';
import { Layout, Row, Col, Typography } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
import { get } from 'lodash';
import { priceFormat, formatStatus } from 'utils';

const { Title, Text } = Typography;
const { Content } = Layout;

function Section({ children }) {
  return (
    <div className="section-container">
      <div className="inner-section">{children}</div>
    </div>
  );
}

const ACTOR_LIST_CLASSNAME = {
  system: 'activity-system',
  homestory: 'activity-admin',
  customer: 'activity-customer',
};

export function InboundOrOutboundOrderDetails({
  children,
  orderDetail = {},
  orderActivity = [],
}) {
  function getOrderDetailValue(path) {
    return get(orderDetail, path, '-');
  }

  function formatDate(str) {
    return dayjs(str).format('DD/MM/YYYY');
  }

  function formatTime(str) {
    return dayjs(str).format('LT');
  }

  function formatMessage(str) {
    if (str) {
      return 'Note: ' + str.charAt(0).toUpperCase() + str.slice(1);
    }
  }

  function renderActor(actor) {
    return (
      <div className={`activity ${ACTOR_LIST_CLASSNAME[actor]}`}>
        {formatStatus(actor)}
      </div>
    );
  }

  const orderSummaryFields = [
    {
      field: 'Invoice Number',
      value: getOrderDetailValue('invoice_number'),
      className: 'pd-color-primary',
    },
    {
      field: 'Order No.',
      value: getOrderDetailValue('format_package_id'),
    },
    {
      field: 'Status',
      value: formatStatus(getOrderDetailValue('status')),
    },
    {
      field: 'Transaction Date',
      value: `${formatDate(getOrderDetailValue('ordered_at'))} - ${formatTime(
        getOrderDetailValue('ordered_at')
      )}`,
    },
    {
      field: 'Customer Name',
      value: getOrderDetailValue('user.first_name'),
    },
    {
      field: 'Customer Phone',
      value: getOrderDetailValue('user.phone_number'),
    },
    {
      field: 'Recipient Name',
      value:
        Object.keys(orderDetail.address || {}).length > 0
          ? getOrderDetailValue('address.recipient')
          : getOrderDetailValue('recipient'),
    },
    {
      field: 'Recipient Phone',
      value:
        Object.keys(orderDetail.address || {}).length > 0
          ? getOrderDetailValue('address.phone_number')
          : getOrderDetailValue('recipient_number'),
    },
    {
      field: 'Recipient Address',
      value: getOrderDetailValue('address.address'),
    },
    {
      field: 'Warehouse',
      value: getOrderDetailValue('warehouse.title'),
    },
    {
      field: 'Pick Up Point',
      value: getOrderDetailValue('pickup_point.title'),
    },
    {
      field: 'Pick Up Point Address',
      value: getOrderDetailValue('pickup_point.address'),
    },
  ];

  return (
    <Layout className="main-container">
      <div className="content">
        <div className="pageTitle">Order Detail</div>
        <Content className="pageContent">
          <Section>
            <Row>
              <Col span={24} className="pd-margin-bottom-md">
                <Title level={4}>Order Summary</Title>
              </Col>
            </Row>
            <Row>
              <Col span={18} className="summaryContent">
                <Row>
                  {orderSummaryFields.map(
                    ({ field, value, className = '' } = {}, idx) => (
                      <Col span={24} key={idx}>
                        <Row gutter={[18, 36]}>
                          <Col span={6}>{field}</Col>
                          <Col span={12}>
                            <Text className={className} strong>
                              {value}
                            </Text>
                          </Col>
                        </Row>
                      </Col>
                    )
                  )}
                </Row>
              </Col>
              <Col span={6}>
                {/*  CTA buttons here */}
                {children}
              </Col>
            </Row>
          </Section>

          <Section>
            <Title level={4}>Products</Title>

            {get(orderDetail, 'order_products', []).map((op, index) => (
              <div key={index} className="product-list">
                <Col span={4}>
                  <img
                    src={op.product.preview.secure_url}
                    alt={op.product.preview.title}
                    className="scale-img"
                  />
                </Col>
                <Col span={13}>
                  <div>
                    <Text strong>{op.product.title}</Text>
                  </div>
                  <div className="fs-14">
                    <Text>
                      {op.quantity} {op.quantity > 1 ? 'products' : 'product'} x
                      IDR {priceFormat(op.price)}
                    </Text>
                  </div>
                  {typeof get(op.color, 'color') !== 'undefined' && (
                    <div className="fs-14">
                      <Text>Color: {get(op.color, 'color')}</Text>
                    </div>
                  )}
                </Col>
                <Col span={4}>
                  <Text strong>IDR {priceFormat(op.quantity * op.price)}</Text>
                </Col>
              </div>
            ))}
            <div className="totalSummary">
              <Row>
                <Col span={15} className="left">
                  <Text>Total Price</Text>
                </Col>
                <Col span={4} offset={1}>
                  <Text strong>
                    IDR {priceFormat(get(orderDetail, 'total_price', '0'))}
                  </Text>
                </Col>

                {get(orderDetail, 'promo_discount', '0') !== '0.0' && (
                  <>
                    <Col span={15} className="left">
                      <Text>Promo </Text>
                      <Text className="promo">
                        ({get(orderDetail, `promo.code`, '0')})
                      </Text>
                    </Col>
                    <Col span={4} offset={1}>
                      <Text strong className="promo price">
                        - IDR{' '}
                        {priceFormat(get(orderDetail, 'promo_discount', '0'))}
                      </Text>
                    </Col>
                  </>
                )}

                <Col span={15} className="left">
                  <Text>Total Payment</Text>
                </Col>
                <Col span={4} offset={1}>
                  <Text strong>
                    IDR {priceFormat(get(orderDetail, 'net_price', '0'))}
                  </Text>
                </Col>
              </Row>
            </div>
          </Section>
          <Section>
            <Title level={4}>Order Status</Title>
            {orderActivity.map((activity, index) => (
              <div key={index} className="activityContent">
                <Col span={5}>
                  {renderActor(get(activity, 'actor.full_name', ''))}
                </Col>
                <Col span={10} offset={1}>
                  {formatStatus(get(activity, 'verb', ''))}
                  <br />
                  {formatMessage(get(activity, 'message', ''))}
                </Col>
                <Col span={10} className="fs-14">
                  {formatDate(get(activity, 'created_at', ''))} -{' '}
                  {formatTime(get(activity, 'created_at', ''))}
                </Col>
              </div>
            ))}
          </Section>
        </Content>
      </div>
    </Layout>
  );
}
